import { productImageAlt } from "@/lib/imageAlt";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Minus, Plus, Trash2, Loader2, X } from "lucide-react";
import { BrandBag } from "./BrandIcons";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice, getShopifyImageUrl } from "@/lib/shopify";
import { lineImageUrl } from "@/lib/variants";
import { FreeShippingProgress } from "@/components/FreeShippingProgress";
import { PromoCodeInput } from "@/components/PromoCodeInput";
import { TrustBadges } from "@/components/TrustBadges";
import { PROMO_CODES_ENABLED } from "@/lib/promo";
import { CheckoutDomainNotice } from "@/components/CheckoutDomainNotice";
import { CartUpsell } from "@/components/CartUpsell";
import { startPartnerCheckout } from "@/lib/checkout";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const {
    items,
    notices,
    removedNotices,
    isLoading,
    isSyncing,
    updateQuantity,
    removeItem,
    getCheckoutUrl,
    syncCart,
    dismissRemovedNotices,
    getTotalItems,
    getTotalPrice,
  } = useCartStore();

  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  // The first-order code and the badges sit under the Checkout button when the
  // screen has the height for them, and at the end of the scrolling lines when
  // it does not. Pinned on every screen they left 39px for the cart lines on a
  // 375x667 phone and 12px at 360x640.
  const firstOrderCode = (
    <p className="text-center text-[12px] text-muted-foreground">First order? Use code <span className="font-semibold text-primary tracking-wider">LOFTLOOM10</span> at checkout for 10% off.</p>
  );

  const handleCheckout = async () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      setIsOpen(false);
      // In-place navigation: a popup opened after async cart work is silently
      // blocked on mobile Safari and Chrome, so the button appears dead.
      await startPartnerCheckout('drawer');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-current hover:text-primary hover:bg-transparent [&_svg]:size-5" aria-label="Shopping cart">
          <BrandBag className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 flex items-center justify-center text-[10px] bg-primary text-primary-foreground border-0 animate-in zoom-in-75 duration-200">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full sm:max-w-md flex flex-col h-full bg-background border-l border-border">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display text-2xl font-semibold">Cart</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''}`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {/* Lines that sold out or left the cart elsewhere. Shown over an emptied cart too, so it explains itself. */}
          {removedNotices.length > 0 && (
            <div role="status" className="flex-shrink-0 mb-4 flex items-start justify-between gap-2 rounded-xl border border-border bg-secondary/40 py-1 pl-3 pr-1 text-xs text-foreground">
              <div className="space-y-1 py-3">
                {removedNotices.map(({ variantId, message }) => (
                  <p key={variantId}>{message}</p>
                ))}
              </div>
              <button
                aria-label="Dismiss"
                className="min-h-[44px] min-w-[44px] flex-shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                onClick={dismissRemovedNotices}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-secondary/50 flex items-center justify-center">
                  <BrandBag className="h-7 w-7 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-foreground font-medium">Your cart is empty</p>
                  <p className="text-sm text-muted-foreground mt-1">Explore our collection to find something you love.</p>
                </div>
                <Button
                  variant="outline"
                  className="rounded-full text-[12px] tracking-widest uppercase"
                  onClick={() => setIsOpen(false)}
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-6">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4">
                      <div className="w-20 h-24 rounded-control bg-sand overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={getShopifyImageUrl(lineImageUrl(item.product.node, item.variantId)!, 300)}
                            alt={productImageAlt(item.product.node.title)}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium line-clamp-1">{item.product.node.title}</h4>
                        {item.variantTitle !== "Default Title" && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {item.selectedOptions.map(option => option.value).join(' · ')}
                          </p>
                        )}
                        <p className="text-sm font-semibold tabular-nums mt-1.5">
                          {formatPrice(item.price.amount, item.price.currencyCode)}
                        </p>

                        <div className="flex items-center justify-between mt-3">
                          {/* The stepper waits for a pending change, as on /cart. */}
                          <div className="flex items-center gap-3">
                            <button
                              aria-label="Decrease quantity"
                              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                              disabled={isLoading}
                              onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="text-sm w-5 text-center">{item.quantity}</span>
                            <button
                              aria-label="Increase quantity"
                              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                              disabled={isLoading}
                              onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <button
                            aria-label="Remove item"
                            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                            onClick={() => removeItem(item.variantId)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {notices[item.variantId] && (
                          <p role="status" className="mt-1 rounded-lg bg-secondary/60 px-2.5 py-1.5 text-xs text-foreground">
                            {notices[item.variantId].message}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Upsell suggestions */}
                <CartUpsell cartItems={items} />

                <div className="mt-6 space-y-3 [@media(min-height:800px)]:hidden">
                  {firstOrderCode}
                  <TrustBadges />
                </div>
              </div>

              <div className="flex-shrink-0 pt-4 border-t border-border mt-4 space-y-3 [@media(min-height:800px)]:pt-6 [@media(min-height:800px)]:mt-6">
                <FreeShippingProgress 
                  currentTotal={totalPrice - discountAmount} 
                  currencyCode={items[0]?.price.currencyCode}
                />

                {/* Promo code — off until real codes reach checkout (see lib/promo). */}
                {PROMO_CODES_ENABLED && (
                <PromoCodeInput
                  cartTotal={totalPrice}
                  onApply={(code, amount) => {
                    setAppliedCode(code);
                    setDiscountAmount(amount);
                  }}
                  onRemove={() => {
                    setAppliedCode(null);
                    setDiscountAmount(0);
                  }}
                  appliedCode={appliedCode}
                  discountAmount={discountAmount}
                />
                )}

                {/* Subtotal */}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Subtotal</span>
                  <span className="text-sm font-medium">
                    {formatPrice(totalPrice.toString(), items[0]?.price.currencyCode || 'USD')}
                  </span>
                </div>

                {/* Discount row */}
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center text-primary">
                    <span className="text-sm">Discount ({appliedCode})</span>
                    <span className="text-sm font-medium">
                      −{formatPrice(discountAmount.toString(), items[0]?.price.currencyCode || 'USD')}
                    </span>
                  </div>
                )}

                {/* Total */}
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center border-t border-border pt-2">
                    <span className="text-sm font-bold">Total</span>
                    <span className="text-sm font-bold">
                      {formatPrice((totalPrice - discountAmount).toString(), items[0]?.price.currencyCode || 'USD')}
                    </span>
                  </div>
                )}

                <Button
                  onClick={handleCheckout}
                  variant="purchase"
                  className="w-full h-[52px]"
                  size="lg"
                  disabled={items.length === 0 || isLoading || isSyncing}
                >
                  {isLoading || isSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Checkout"
                  )}
                </Button>

                <div className="hidden [@media(min-height:800px)]:block">{firstOrderCode}</div>
                <CheckoutDomainNotice placement="drawer" />

                {/* Trust badges */}
                <div className="hidden [@media(min-height:800px)]:block">
                  <TrustBadges />
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
